/**
 * Живые потоки сервера (Server-Sent Events):
 *  - useJobStream(id) — события одной задачи («Что делает Claude»);
 *  - useGlobalStream() — общий поток: задачи начались/закончились → уведомления и обновление данных.
 */
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { GlobalStreamMessage, JobEvent, JobStreamMessage, JobSummary } from "@impuls/shared";
import { JOB_TYPE_LABEL } from "@impuls/shared";
import { countText } from "./format";
import { useToast } from "../components/ui";

export interface JobStreamState {
  job: JobSummary | null;
  events: JobEvent[];
  connected: boolean;
  /** поток закрылся, не прислав снимка: задачи больше нет (старые чистятся) */
  failed: boolean;
}

/** Подписка на события задачи. Возвращает накопленные события (обновлённые по id) и статус задачи. */
export function useJobStream(jobId: number | null | undefined, onDone?: (job: JobSummary) => void): JobStreamState {
  const [state, setState] = useState<JobStreamState>({ job: null, events: [], connected: false, failed: false });
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!jobId) {
      setState({ job: null, events: [], connected: false, failed: false });
      return;
    }
    let finished = false;
    let snapshot = false;
    const es = new EventSource(`/api/jobs/${jobId}/events`);
    const upsert = (list: JobEvent[], e: JobEvent) => {
      const i = list.findIndex((x) => x.id === e.id);
      if (i === -1) return [...list, e].sort((a, b) => a.id - b.id);
      const copy = list.slice();
      copy[i] = e;
      return copy;
    };
    es.onopen = () => setState((s) => ({ ...s, connected: true }));
    es.onmessage = (ev) => {
      let msg: JobStreamMessage;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (msg.type === "snapshot") {
        snapshot = true;
        setState({ job: msg.job, events: msg.events, connected: true, failed: false });
        if (msg.job.status !== "running" && !finished) {
          finished = true;
          es.close();
          doneRef.current?.(msg.job);
        }
      } else if (msg.type === "event") {
        setState((s) => ({ ...s, events: upsert(s.events, msg.event) }));
      } else if (msg.type === "job") {
        setState((s) => (s.job && msg.job.id !== s.job.id ? s : { ...s, job: msg.job }));
        if (msg.job.id === jobId && msg.job.status !== "running" && !finished) {
          finished = true;
          es.close();
          doneRef.current?.(msg.job);
        }
      }
    };
    es.onerror = () => {
      // 404 и другие ответы не 200: EventSource закрылся насовсем (CLOSED = 2); обрыв связи — переподключится сам
      const gone = !snapshot && es.readyState === 2;
      setState((s) => ({ ...s, connected: false, failed: s.failed || gone }));
      if (finished || gone) es.close();
    };
    return () => {
      finished = true;
      es.close();
    };
  }, [jobId]);

  return state;
}

const AUTO_TYPES = new Set(["auto:schedule", "auto:news", "auto:fact", "auto:myth", "auto:idea"]);
/** Что перечитать после переподключения: пока потока не было, события могли пройти мимо. */
const RESYNC_KEYS = [["stats"], ["jobs"], ["auto-status"], ["posts"], ["calendar"]];

/** Общий поток: уведомления о завершении фоновых задач и обновление данных. Подключается один раз в оболочке. */
export function useGlobalStream() {
  const qc = useQueryClient();
  const toast = useToast();
  useEffect(() => {
    let es: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let greeted = false;
    const connect = () => {
      es = new EventSource("/api/stream");
      es.onmessage = (ev) => {
        let msg: GlobalStreamMessage;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (msg.type === "hello") {
          if (greeted) for (const queryKey of RESYNC_KEYS) void qc.invalidateQueries({ queryKey });
          greeted = true;
          return;
        }
        if (msg.type === "stats") {
          // посты меняют статус и без задачи (вышел по расписанию, не нашёлся во ВК) — списки тоже
          void qc.invalidateQueries({ queryKey: ["stats"] });
          void qc.invalidateQueries({ queryKey: ["posts"] });
          void qc.invalidateQueries({ queryKey: ["calendar"] });
          return;
        }
        if (msg.type !== "job") return;
        const job = msg.job;
        void qc.invalidateQueries({ queryKey: ["stats"] });
        void qc.invalidateQueries({ queryKey: ["jobs"] });
        void qc.invalidateQueries({ queryKey: ["auto-status"] });
        if (job.status === "running") return;
        if (job.post_id) void qc.invalidateQueries({ queryKey: ["post", job.post_id] });
        void qc.invalidateQueries({ queryKey: ["posts"] });
        void qc.invalidateQueries({ queryKey: ["calendar"] });
        // уведомляем только о долгих задачах верхнего уровня (автоконтент)
        if (!AUTO_TYPES.has(job.type) || job.parent_id) return;
        if (job.status === "done") {
          const n = job.post_ids.length;
          toast(
            n ? `${JOB_TYPE_LABEL[job.type]}: готово ${countText(n, "пост", "поста", "постов")}` : `${JOB_TYPE_LABEL[job.type]}: готово`,
            "ok",
            n ? { label: "Открыть", to: n === 1 ? `/posts/${job.post_ids[0]}` : "/auto" } : undefined,
          );
        } else {
          toast(`${JOB_TYPE_LABEL[job.type]}: ${job.info}`, "error", { label: "Открыть", to: "/auto" });
        }
      };
      es.onerror = () => {
        es?.close();
        retry = setTimeout(connect, 5000);
      };
    };
    connect();
    return () => {
      if (retry) clearTimeout(retry);
      es?.close();
    };
  }, [qc, toast]);
}
